"use client";

import "./globals.css";
import "../styles/themes.css";
import { ThemeWrapper } from "@/components/ThemeWrapper";
import { ThemeInitScript } from "@/components/ThemeInitScript";
import { AuthenticationProvider } from "@/contexts/AuthenticationContext";
import { DemoProvider } from "@/app/contexts/DemoContext";
import { Analytics } from "@vercel/analytics/react";
import versionData from "./version.json";
import { SpeedInsights } from "@vercel/speed-insights/next";
import { Toaster } from "@/components/ui/toaster";
import { CookieConsentClientProvider } from "@/components/CookieConsentClientProvider";
import { CookieConsentBanner } from "@/components/analytics/CookieConsentBanner";

import { ConditionalClarity } from "@/components/analytics/ConditionalClarity";
import { ConditionalGoogleTagManager } from "@/components/analytics/ConditionalGoogleTagManager";
import { TopNavigationWrapper } from "@/components/topNavigationWrapper/page";
import { SiteNotificationBanner } from "@/components/SiteNotificationBanner";
import { ErrorBoundary } from "@sentry/nextjs";
import { SentryUserInitializer } from "@/components/SentryUserInitializer";

/**
 * Root layout component for the Story Stream application.
 *
 * This component:
 * 1. Provides the base HTML structure
 * 2. Includes necessary meta tags
 * 3. Sets up theme support
 * 4. Renders the top navigation
 * 5. Provides the main content area
 */

export default function RootLayoutClient({
    children,
}: {
    children: React.ReactNode;
}) {
    console.debug(`Story Stream Web UI - Version ${versionData.version}`);

    const htmlStyle: React.CSSProperties = typeof window === 'undefined' ? { visibility: 'hidden' } : {};

    return (
        <html lang="en" suppressHydrationWarning style={htmlStyle}>
            <head>
                <ThemeInitScript />
            </head>
      <body className="font-sans antialiased">
        <ThemeWrapper>
          <AuthenticationProvider>
            <DemoProvider>
              <SentryUserInitializer />
              <CookieConsentClientProvider>
                <ErrorBoundary>
                <div className="min-h-screen">
                  <TopNavigationWrapper/>
                  <SiteNotificationBanner />
                  {children}
                  <SpeedInsights />
                  <ConditionalClarity />
                  <ConditionalGoogleTagManager />
                  <CookieConsentBanner />
                </div>
              </ErrorBoundary>
              <Toaster />
            </CookieConsentClientProvider>
              </DemoProvider>
            </AuthenticationProvider>
          </ThemeWrapper>
      </body>
      <Analytics />
    </html>
  );
}
